import type { SimEvent } from '@prism/sim';

/**
 * Fast-forward a wave in which nothing is taking damage (playtest request).
 *
 * The reported case: a player who stacks every tower near the spawn watches anything that
 * survives the gauntlet walk the remaining forty tiles unopposed. The wave is decided,
 * and there is still most of a minute of watching left.
 *
 * The test is simply "did anything take damage recently", which is broader and far more
 * honest than asking whether any tower has a target: poison ticking, a zone burning, a
 * drone chasing and a leak landing on the Core all count as something happening, with no
 * list of exceptions to keep in sync. If nothing has been hurt for two seconds, nothing
 * is happening.
 *
 * Speed is pure presentation — it scales how much wall time feeds a FIXED-step
 * accumulator, and the sim still steps at DT — so this shortens the WAIT and not one tick
 * of the run the server replays. The rolling hash cannot tell the difference.
 */

/** Quiet for this long and time speeds up. */
export const IDLE_SKIP_AFTER_MS = 2000;

/**
 * Four times the fastest speed the player can pick, deliberately.
 *
 * The point is to make the dead stretch disappear rather than merely shorten it; at 4×
 * a forty-tile walk is still ten seconds of watching nothing. It overrides whatever they
 * chose and hands that choice straight back the moment damage lands again.
 */
export const IDLE_SKIP_SPEED = 16;

/** Anything that means a body was hurt — enemy, or the Core taking a leak. */
export function isDamageEvent(ev: SimEvent): boolean {
  return ev.t === 'enemyDamaged' || ev.t === 'coreDamaged' || ev.t === 'enemyKilled';
}

export class IdleSkip {
  private quietMs = 0;
  engaged = false;

  /** Something was hurt. Resets the clock and drops the skip on the same frame. */
  noteDamage(): void {
    this.quietMs = 0;
    this.engaged = false;
  }

  /** Feed a tick's events; any damage among them restarts the wait. */
  noteEvents(events: readonly SimEvent[]): void {
    for (const ev of events) {
      if (isDamageEvent(ev)) {
        this.noteDamage();
        return;
      }
    }
  }

  /** @returns true when the run should be fast-forwarded this frame. */
  update(phase: string, deltaMs: number): boolean {
    // Combat only. The build phase is quiet by definition, and racing through it would
    // run the autostart countdown at sixteen times its own clock.
    if (phase !== 'combat') {
      this.reset();
      return false;
    }
    this.quietMs += deltaMs;
    this.engaged = this.quietMs >= IDLE_SKIP_AFTER_MS;
    return this.engaged;
  }

  reset(): void {
    this.quietMs = 0;
    this.engaged = false;
  }
}
